import { ExternalLink as ExternalLinkIcon } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

interface ExternalLinkProps {
  /** URL de destino */
  href: string
  /** Nome da plataforma (exibido no tooltip) */
  platform?: string
  /** Conteúdo do link */
  children: React.ReactNode
  /** Classes CSS adicionais */
  className?: string
  /** Mostrar ícone de link externo */
  showIcon?: boolean
}

/**
 * Componente reutilizável para links externos
 *
 * - Abre em nova aba com `target="_blank"`
 * - Inclui `rel="noopener noreferrer"` por segurança
 * - Exibe ícone de link externo (opcional)
 * - Tooltip com nome da plataforma (opcional)
 *
 * @example
 * <ExternalLink href="https://example.com" platform="Guru">
 *   #12345
 * </ExternalLink>
 */
export function ExternalLink({
  href,
  platform,
  children,
  className,
  showIcon = true,
}: ExternalLinkProps) {
  const link = (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'inline-flex items-center gap-1 text-primary hover:underline hover:text-primary/80 transition-colors',
        className
      )}
      aria-label={platform ? `Abrir no ${platform}` : 'Abrir link externo'}
    >
      {children}
      {showIcon && <ExternalLinkIcon className="h-3 w-3 shrink-0" aria-hidden="true" />}
    </a>
  )

  if (!platform) {
    return link
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent>
          <p>Abrir no {platform}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
